import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { type UseModalState } from "@src/lib/ui-react";
import { type ReactNode } from "react";

import { useIsMobile } from "../AppLayout/useResponsive";
import { Divider } from "../components/Divider";
import { SIDE_PANEL_HEADER_MIN_HEIGHT } from "../components/SidePanel";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { ChatPanelMessageContent } from "./ChatPanelMessageContent";
import { ChatAdminReadOnly } from "./components/ChatAdminReadOnly";
import { ChatDeleteMessageDialog } from "./components/ChatDeleteMessageDialog";
import { ChatEditPreview } from "./components/ChatEditPreview";
import { FcmRequestDialogHost } from "./components/FcmRequestDialogHost";
import { ChatReplyPreview } from "./components/ChatReplyPreview";
import { useShareWorkerCredentials } from "./components/ShareCredentials/useShareWorkerCredentials";
import { useChatInvite } from "./hooks/useChatInvite";
import { useChatPanelState } from "./hooks/useChatPanelState";
import { useTu } from "@src/appV2/i18n/useTu";

export interface ChatPanelProps {
  modalState: UseModalState;
  /**
   * Workplace ID for the chat channel. When omitted, defaults to the
   * logged-in user's profile ID from the session.
   */
  workplaceId?: string;
  /**
   * Worker ID for API integration to fetch/send chat messages
   */
  workerId: string;
  workerName: string;
  /**
   * When provided, loads the placement-specific channel (3-part URL).
   */
  placementId?: string;
  /**
   * Optional callback when chat panel is closed
   */
  onClose?: () => void;
  /**
   * Optional callback when "View Profile" is clicked in the chat header.
   * When provided, the parent handles opening the worker profile (e.g. replacing panels).
   * When omitted, defaults to navigating to the worker profile page.
   */
  onViewProfile?: (workerId: string) => void;
  /**
   * Whether to show the "View Profile" button in the chat header.
   * Defaults to true. Set to false when the chat opens on a page that already shows the worker profile.
   */
  showViewProfile?: boolean;
  /**
   * Optional fixed context content rendered below the header and above the
   * scrollable message log.
   */
  contextContent?: ReactNode;
  /**
   * Forwarded to the invite-to-shift dialog's qualification slot. Chat
   * surfaces with channel metadata can pass `metadata.agentReq` so the
   * dialog can default the qualification filter.
   */
  channelQualification?: string;
}

/**
 * Chat panel component for worker communication.
 * Displays chat messages with a worker and provides an input to send new messages.
 * Connected to Sendbird for real-time messaging.
 */
export function ChatPanel(props: ChatPanelProps) {
  const { tu } = useTu("chat");
  const isMobile = useIsMobile();
  const {
    modalState,
    workplaceId: workplaceIdFromCaller,
    workerId,
    workerName,
    placementId,
    onClose,
    onViewProfile,
    showViewProfile,
    contextContent,
    channelQualification,
  } = props;

  const {
    workplaceId,
    isAdmin,
    isWorkerSession,
    isLoading,
    isError,
    isBlocked,
    isDeactivated,
    isSendingDisabled,
    messages,
    hasMore,
    isLoadingMore,
    exclusion,
    replyTo,
    editingMessage,
    deleteModalState,
    isDeleting,
    handleClose,
    handleSend,
    handleFileSelect,
    handleRetry,
    handleMarkMessageAsUnread,
    handleMarkConversationAsUnread,
    canMarkConversationAsUnread,
    loadMore,
    handleReply,
    clearReply,
    handleEdit,
    clearEditing,
    handleDelete,
    handleDeleteConfirm,
    handleDeleteCancel,
  } = useChatPanelState({
    modalState,
    workplaceId: workplaceIdFromCaller,
    workerId,
    placementId,
    onClose,
  });

  // Deactivation makes the chat read-only just like a DNR block; fold both into
  // one flag so the composer, reply/edit previews, and mark-unread affordances
  // are gated identically.
  const isChatReadOnly = isBlocked || isDeactivated;
  const canEditOrDelete = !isAdmin && !isChatReadOnly && !isWorkerSession;

  const session = useSession();
  const facilityTimeZone = session.profile.tmz || undefined;
  const workplaceUserId = session.user?._id;

  const { onInvite, inviteLabel, inviteDialogElement } = useChatInvite({
    workerId,
    workerName,
    workplaceId,
    placementId,
    channelQualification,
  });

  const { canShareCredentials, openShareCredentialsDialog, shareCredentialsDialogElement } =
    useShareWorkerCredentials({
      workerId,
      workplaceId,
      workerName,
      surfaceDisabled: isDefined(placementId),
    });

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Box
        sx={{
          flexShrink: 0,
          paddingX: 5,
          // Fixed header height only matters on desktop, where panels render
          // side-by-side and their headers must line up. On mobile the chat is
          // always standalone, so keep the original padding and natural height.
          ...(isMobile
            ? { paddingTop: 5, paddingBottom: 4 }
            : ({
                paddingY: 1,
                // Account for the trailing 1px Divider so the total header
                // height matches the other side panels (whose border is inside
                // the box).
                minHeight: `calc(${SIDE_PANEL_HEADER_MIN_HEIGHT} - 1px)`,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              } as const)),
        }}
      >
        <ChatHeader
          workerId={workerId}
          workplaceId={workplaceId}
          workerName={workerName}
          isWorkerSession={isWorkerSession}
          showViewProfile={showViewProfile}
          onClose={handleClose}
          onViewProfile={onViewProfile}
          onMarkConversationAsUnread={
            canMarkConversationAsUnread && !isAdmin && !isChatReadOnly && !isWorkerSession
              ? handleMarkConversationAsUnread
              : undefined
          }
        />
      </Box>

      <Divider />

      {contextContent ? (
        <>
          <Box sx={{ flexShrink: 0, padding: 5, paddingBottom: 0 }}>{contextContent}</Box>
          <Divider sx={{ marginTop: 5 }} />
        </>
      ) : null}

      <FcmRequestDialogHost>
        <Box
          role="log"
          aria-label={tu("Chat messages")}
          aria-live="polite"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: 5,
            display: "flex",
            flexDirection: "column-reverse",
            gap: 4,
          }}
        >
          <ChatPanelMessageContent
            messages={messages}
            workerName={workerName}
            workplaceId={workplaceId}
            workplaceUserId={workplaceUserId}
            facilityTimeZone={facilityTimeZone}
            isLoading={isLoading}
            isError={isError}
            isBlocked={isBlocked}
            isDeactivated={isDeactivated}
            isAdmin={isAdmin}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            exclusion={exclusion}
            onRetry={handleRetry}
            onLoadMore={loadMore}
            onReply={handleReply}
            onMarkAsUnread={
              isAdmin || isChatReadOnly || isWorkerSession ? undefined : handleMarkMessageAsUnread
            }
            onEdit={canEditOrDelete ? handleEdit : undefined}
            onDelete={canEditOrDelete ? handleDelete : undefined}
          />
        </Box>
      </FcmRequestDialogHost>

      {editingMessage && canEditOrDelete && (
        <ChatEditPreview message={editingMessage.originalText} onCancel={clearEditing} />
      )}

      {replyTo && !editingMessage && !isAdmin && !isChatReadOnly && (
        <ChatReplyPreview
          message={replyTo.message}
          senderName={replyTo.senderName}
          onCancel={clearReply}
        />
      )}

      {!isChatReadOnly && (
        <Box sx={{ flexShrink: 0, padding: 5 }}>
          {isAdmin ? (
            <ChatAdminReadOnly onInviteToShift={onInvite} inviteLabel={inviteLabel} />
          ) : (
            <ChatInput
              key={editingMessage?.messageId ?? "compose"}
              disabled={isSendingDisabled}
              initialValue={editingMessage?.originalText}
              onFileSelect={editingMessage ? undefined : handleFileSelect}
              onInviteToShift={editingMessage ? undefined : onInvite}
              inviteLabel={inviteLabel}
              onShareCredentials={
                editingMessage || !canShareCredentials ? undefined : openShareCredentialsDialog
              }
              onSend={handleSend}
            />
          )}
        </Box>
      )}

      <ChatDeleteMessageDialog
        isDeleting={isDeleting}
        modalState={deleteModalState}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      {inviteDialogElement}
      {shareCredentialsDialogElement}
    </Box>
  );
}
